import React, { useContext, useEffect, useRef, useState } from "react";
import StackWrapper from "../../StackWrapper";
import Label from "../../../../../common/components/Label";
import {
  Autocomplete,
  AutocompleteInputChangeReason,
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import { ISelectedFolderDetails } from "./CampaignSetupForm";
import {
  useGetFoldersListMutation,
  usePostCreateFolderMutation,
} from "../../../../../redux/services/campaignSetupService";
import { IFolderDetails } from "../../../../../types/campaign.types";
import { SEARCH_ICON } from "../../../../../constants/icons/SearchIcon";
import { useSelector } from "react-redux";
import { userDataSelector } from "../../../../../redux/selectors/user.selector";
import { dateFormatter } from "../../../../../utils/helpers";
import { ToastContext } from "../../../../../utils/context/toastContext";
import { getErrorMsg, getIsTriggeredFromFolder } from "../../../../../utils/whatsAppUtils";

interface IProps extends ISelectedFolderDetails {}

const SaveInFolder: React.FC<IProps> = ({
  handleUpdateSelectedFolder,
  selectedFolder,
  error,
  isBulkCampaign,
  triggeredFromPage,
  folderId,
  folders,
  fetchFoldersStatus
}) => {
  const [inputValue, setInputValue] = useState<string>("");
  const [options, setOptions] = useState<IFolderDetails[]>([]);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const { userData } = useSelector(userDataSelector);

  const { setToastValue } = useContext(ToastContext);

  const [
    postCreateFolder,
    {
      isLoading: createFolderLoading,
      isSuccess: createFolderSuccess,
      isError: createFolderIsError,
      error: createFolderError,
      reset,
    },
  ] = usePostCreateFolderMutation();

  const [triggerGetFoldersList, { data: foldersList, isLoading, isSuccess }] =
    useGetFoldersListMutation();

  const handleInputChange = (
    event: React.SyntheticEvent,
    value: string,
    reason: AutocompleteInputChangeReason
  ) => {
    setInputValue(value);

    // make api call only when input changes by typing, not by selecting option
    if (reason === "input") {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      // don't make api when input is cleared
      if (value) {
        debounceTimeoutRef.current = setTimeout(() => {
          triggerGetFoldersList({ folderName: value });
        }, 500);
      }
    }

    // reset option when input is empty
    if (!value) {
      setOptions([]);
      handleUpdateSelectedFolder(null);
    }
  };

  const handleSelectionChange = (
    event: React.SyntheticEvent,
    value: string | IFolderDetails | null
  ) => {
    if (value && typeof value !== "string") {
      handleUpdateSelectedFolder(value as IFolderDetails);
      setOptions([value as IFolderDetails]);
    }
  };

  const handleCreateFolder = () => {
    const payload = {
      folderName: inputValue,
      channelId: userData.channelId,
      corpId: userData.corpId,
      subChannelId: userData.subChannelId,
      subUid: userData.recruiterId,
    };

    postCreateFolder(payload);
  };

  useEffect(()=>{
    // update the options, when options lists is empty and selectedFolder is present
    if(!options?.length && selectedFolder?.folderId){
      setOptions([selectedFolder]);
    }
  },[options?.length, selectedFolder])

  useEffect(() => {
    if (foldersList && isSuccess) {
      // update options based on search result
      setOptions(foldersList);
    }
  }, [isSuccess, foldersList, folderId, handleUpdateSelectedFolder]);

  useEffect(() => {
    if (createFolderSuccess) {
      // updating selected folder and options based on response of create folder
      triggerGetFoldersList({ folderName: inputValue }).then((response) => {
        const result = { ...response } as {
          data: IFolderDetails[];
        };

        if (result?.data?.length) {
          const selectedFolder = result.data[0];
          handleUpdateSelectedFolder(selectedFolder);
          setOptions([selectedFolder]);
        }
      });

      setToastValue({
        isToastShown: true,
        toastMessage: "Folder created successfully.",
        toastKey: "createFolderSuccess",
        toastSeverity: "success",
      });
      reset();
    } else if (createFolderIsError) {
      setToastValue({
        isToastShown: true,
        toastMessage: getErrorMsg({
          error: createFolderError,
          defaultMsg: "Failed to create folder. Please try again.",
        }),
        toastKey: "createFolderError",
        toastSeverity: "error",
      });
      reset();
    }
  }, [
    createFolderIsError,
    createFolderSuccess,
    handleUpdateSelectedFolder,
    inputValue,
    setToastValue,
    triggerGetFoldersList,
    reset,
    createFolderError,
  ]);

  return (
    <StackWrapper>
      <Label
        colorType="light"
        required
        label="Save in folder"
        labelFor="saveInFolder"
      />

      <Autocomplete
        value={selectedFolder?.name || ""}
        onChange={handleSelectionChange}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        options={options}
        getOptionLabel={(option) =>
          typeof option === "string" ? option : option.name
        }
        loading={isLoading}
        disableClearable
        disabled={
          createFolderLoading || fetchFoldersStatus==="pending" || 
          (isBulkCampaign &&  getIsTriggeredFromFolder(triggeredFromPage))
        }
        renderOption={(props, option) => (
          <li {...props}>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography variant="h6">{`${option.name} (${option.num_profiles})`}</Typography>
              <Typography sx={{ color: "grey", fontSize: 10 }}>
                {dateFormatter(option.folder_created)}
              </Typography>
            </Box>
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Search by folder name"
            variant="outlined"
            fullWidth
            InputProps={{
              ...params.InputProps,
              startAdornment: <Box sx={{ pl: 1 }}>{SEARCH_ICON}</Box>,
              endAdornment: (
                <>
                  {isLoading || createFolderLoading || fetchFoldersStatus==="pending" ? (
                    <CircularProgress
                      sx={{ mr: 2 }}
                      color="inherit"
                      size={20}
                    />
                  ) : null}

                  {params.InputProps.endAdornment}

                  {(!isLoading && !createFolderLoading && fetchFoldersStatus!=="pending") && (
                    <Button
                      size="small"
                      variant="text"
                      sx={{ textTransform: "none" }}
                      disabled={
                        !inputValue ||
                        !!options?.filter((item) => item.name === inputValue)
                          ?.length
                      }
                      onClick={() => handleCreateFolder()}
                      disableRipple
                    >
                      Create
                    </Button>
                  )}
                </>
              ),
            }}
            error={!!error && !selectedFolder}
            helperText={!!error && !selectedFolder ? error : ""}
          />
        )}
        isOptionEqualToValue={(option, value) =>
          option.folderId === value.folderId
        }
        freeSolo
      />
    </StackWrapper>
  );
};

export default React.memo(SaveInFolder);
